import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { credentialRequestsAPI, providerCompaniesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Building2, 
  User, 
  Users, 
  CreditCard,
  Plus,
  Trash2,
  Upload,
  CheckCircle,
  FileText,
  Camera
} from 'lucide-react';

const PRODUCT_CATEGORIES = [
  'Medicamentos',
  'OTC',
  'Suplementos',
  'Cosméticos',
  'Dispositivos Médicos',
  'Higiene Personal',
  'Nutrición',
  'Otros'
];

export default function CreateCredentialRequestPage() {
  const navigate = useNavigate();
  const { user, isProvider } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    // Company info
    company_name: '',
    ruc: '',
    commercial_line: '',
    product_categories: [],
    farmaenlace_contact_name: '',
    
    // Signer info
    signer_name: '',
    signer_id: '',
    signer_email: '',
    
    // Credentials
    num_credentials: 1,
    
    // Validity period
    validity_start: '',
    validity_end: '',
    
    // Representatives
    representatives: []
  });

  // Load provider company info on mount if user is a provider
  useEffect(() => {
    loadProviderInfo();
  }, [user]);

  const loadProviderInfo = async () => {
    if (!user?.provider_company_id) {
      setLoadingCompany(false);
      return;
    }
    
    setLoadingCompany(true);
    try {
      const response = await providerCompaniesAPI.getAll();
      const myCompany = response.data.find(c => c.id === user.provider_company_id);
      
      if (myCompany) {
        // Pre-fill company info
        setFormData(prev => ({
          ...prev,
          company_name: myCompany.name || '',
          ruc: myCompany.tax_id || '',
          // Pre-fill signer info from current user
          signer_name: user.name || '',
          signer_email: user.email || '',
        }));
      }
    } catch (error) {
      console.error('Error loading provider info:', error);
    } finally {
      setLoadingCompany(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      product_categories: prev.product_categories.includes(category)
        ? prev.product_categories.filter(c => c !== category)
        : [...prev.product_categories, category]
    }));
  };

  const addRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [...prev.representatives, { name: '', surname: '', id_number: '' }]
    }));
  };

  const updateRepresentative = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives.map((rep, i) => 
        i === index ? { ...rep, [field]: value } : rep
      )
    }));
  };

  const removeRepresentative = (index) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives.filter((_, i) => i !== index)
    }));
  };

  // Handle local photo selection (before submission)
  const handleLocalPhotoSelect = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La foto no puede superar los 5MB');
      return;
    }
    
    // Create preview URL and store file
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives.map((rep, i) => 
        i === index ? { ...rep, photo_preview: previewUrl, photo_file: file } : rep
      )
    }));
  };

  // Remove local photo
  const removeLocalPhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives.map((rep, i) => 
        i === index ? { ...rep, photo_preview: null, photo_file: null } : rep
      )
    }));
  };

  // Validate validity period (minimum 1 month)
  const validateValidityPeriod = () => {
    if (!formData.validity_start || !formData.validity_end) {
      return true; // Not required, can skip
    }
    
    const start = new Date(formData.validity_start);
    const end = new Date(formData.validity_end);
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    
    if (diffDays < 30) {
      toast.error('El período de vigencia debe ser de al menos 1 mes (30 días)');
      return false;
    }
    return true;
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        if (!formData.company_name || !formData.ruc || !formData.commercial_line) {
          toast.error('Por favor complete todos los campos de la empresa');
          return false;
        }
        if (formData.product_categories.length === 0) {
          toast.error('Seleccione al menos una categoría de productos');
          return false;
        }
        return true;
      case 2:
        if (!formData.signer_name || !formData.signer_id || !formData.signer_email) {
          toast.error('Por favor complete todos los datos del firmante');
          return false;
        }
        if (!formData.signer_email.includes('@')) {
          toast.error('Ingrese un email válido');
          return false;
        }
        return true;
      case 3:
        if (formData.num_credentials < 1) {
          toast.error('Debe solicitar al menos 1 credencial');
          return false;
        }
        if (formData.representatives.length === 0) {
          toast.error('Agregue al menos un representante');
          return false;
        }
        for (const rep of formData.representatives) {
          if (!rep.name || !rep.surname) {
            toast.error('Complete el nombre y apellido de todos los representantes');
            return false;
          }
        }
        // Validate validity period if provided
        if (!validateValidityPeriod()) {
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const calculateTotal = () => {
    const unitPrice = 15.00;
    const iva = 0.15;
    const subtotal = formData.num_credentials * unitPrice;
    const ivaAmount = subtotal * iva;
    return { subtotal, ivaAmount, total: subtotal + ivaAmount };
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      // Prepare representatives (without photo_file and photo_preview)
      const cleanRepresentatives = formData.representatives.map(rep => ({
        name: rep.name,
        surname: rep.surname,
        id_number: rep.id_number || null
      }));
      
      const payload = {
        payment_info: {
          company_name: formData.company_name,
          ruc: formData.ruc,
          signer_name: formData.signer_name,
          signer_id: formData.signer_id,
          signer_email: formData.signer_email,
          commercial_line: formData.commercial_line,
          product_categories: formData.product_categories,
          farmaenlace_contact_name: formData.farmaenlace_contact_name,
          validity_start: formData.validity_start || null,
          validity_end: formData.validity_end || null
        },
        num_credentials: formData.num_credentials,
        representatives: cleanRepresentatives
      };
      
      const response = await credentialRequestsAPI.create(payload);
      const requestId = response.data.id;
      
      // Upload photos for representatives that have them
      for (let i = 0; i < formData.representatives.length; i++) {
        const rep = formData.representatives[i];
        if (rep.photo_file) {
          try {
            await credentialRequestsAPI.uploadPhoto(requestId, i, rep.photo_file);
          } catch (photoError) {
            console.error('Error uploading photo for rep', i, photoError);
          }
        }
      }
      
      toast.success('Solicitud de credenciales creada exitosamente');
      navigate(`/credentials/${requestId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la solicitud');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const pricing = calculateTotal();

  // Show loading while fetching provider info
  if (loadingCompany) {
    return (
      <div className="max-w-3xl mx-auto flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-slate-500">Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="create-credential-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/credentials')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nueva Solicitud de Credenciales</h1>
          <p className="text-slate-500">Complete el formulario para solicitar credenciales de acceso</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {[
          { num: 1, label: 'Empresa', icon: Building2 },
          { num: 2, label: 'Firmante', icon: User },
          { num: 3, label: 'Representantes', icon: Users },
          { num: 4, label: 'Confirmar', icon: CheckCircle },
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step >= s.num ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
                }`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <span className={`text-xs mt-1 ${step >= s.num ? 'text-primary font-medium' : 'text-slate-500'}`}>
                {s.label}
              </span>
            </div>
            {idx < 3 && (
              <div className={`flex-1 h-1 mx-2 ${step > s.num ? 'bg-primary' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Company Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Datos de la Empresa
            </CardTitle>
            <CardDescription>Información del laboratorio o empresa que solicita las credenciales</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Razón Social *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => updateFormData('company_name', e.target.value)}
                  placeholder="Laboratorios XYZ S.A."
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ruc">RUC *</Label>
                <Input
                  id="ruc"
                  value={formData.ruc}
                  onChange={(e) => updateFormData('ruc', e.target.value)}
                  placeholder="1790123456001"
                  data-testid="ruc-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="commercial_line">Línea Comercial / División *</Label>
              <Input
                id="commercial_line"
                value={formData.commercial_line}
                onChange={(e) => updateFormData('commercial_line', e.target.value)}
                placeholder="División Farma"
                data-testid="commercial-line-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Categorías de Productos *</Label>
              <div className="flex flex-wrap gap-2">
                {PRODUCT_CATEGORIES.map((category) => (
                  <Badge
                    key={category}
                    variant={formData.product_categories.includes(category) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleCategory(category)}
                    data-testid={`category-${category}`}
                  >
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="farmaenlace_contact">Nombre del Contacto en Farmaenlace (para facturación)</Label>
              <Input
                id="farmaenlace_contact"
                value={formData.farmaenlace_contact_name}
                onChange={(e) => updateFormData('farmaenlace_contact_name', e.target.value)}
                placeholder="Juan Pérez"
                data-testid="farmaenlace-contact-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Signer Info */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Datos del Firmante
            </CardTitle>
            <CardDescription>Persona autorizada para firmar la cotización de auspicio</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signer_name">Nombre Completo *</Label>
              <Input
                id="signer_name"
                value={formData.signer_name}
                onChange={(e) => updateFormData('signer_name', e.target.value)}
                placeholder="María García López"
                data-testid="signer-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signer_id">Número de Cédula *</Label>
                <Input
                  id="signer_id"
                  value={formData.signer_id}
                  onChange={(e) => updateFormData('signer_id', e.target.value)}
                  placeholder="1712345678"
                  data-testid="signer-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signer_email">Email *</Label>
                <Input
                  id="signer_email"
                  type="email"
                  value={formData.signer_email}
                  onChange={(e) => updateFormData('signer_email', e.target.value)}
                  placeholder="maria.garcia@empresa.com"
                  data-testid="signer-email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Representatives */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Representantes y Credenciales
            </CardTitle>
            <CardDescription>Agregue los representantes que recibirán credenciales</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="num_credentials">Número de Credenciales *</Label>
                <Input
                  id="num_credentials"
                  type="number"
                  min="1"
                  value={formData.num_credentials}
                  onChange={(e) => updateFormData('num_credentials', parseInt(e.target.value) || 1)}
                  data-testid="num-credentials-input"
                />
                <p className="text-sm text-slate-500">$15.00 + IVA por cada credencial</p>
              </div>
            </div>
            
            {/* Validity Period */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Período de Vigencia
              </h4>
              <p className="text-sm text-blue-700">Seleccione el período de validez de las credenciales (mínimo 1 mes)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="validity_start">Fecha Inicio *</Label>
                  <Input
                    id="validity_start"
                    type="date"
                    value={formData.validity_start}
                    onChange={(e) => updateFormData('validity_start', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="validity-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validity_end">Fecha Fin *</Label>
                  <Input
                    id="validity_end"
                    type="date"
                    value={formData.validity_end}
                    onChange={(e) => updateFormData('validity_end', e.target.value)}
                    min={formData.validity_start || new Date().toISOString().split('T')[0]}
                    data-testid="validity-end-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium">Lista de Representantes</h4>
                <Button variant="outline" size="sm" onClick={addRepresentative} data-testid="add-rep-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
              
              {formData.representatives.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No hay representantes agregados</p>
                  <Button variant="link" onClick={addRepresentative}>
                    Agregar primer representante
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.representatives.map((rep, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">Representante {index + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeRepresentative(index)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-rep-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <Input
                          placeholder="Nombre"
                          value={rep.name}
                          onChange={(e) => updateRepresentative(index, 'name', e.target.value)}
                          data-testid={`rep-name-${index}`}
                        />
                        <Input
                          placeholder="Apellido"
                          value={rep.surname}
                          onChange={(e) => updateRepresentative(index, 'surname', e.target.value)}
                          data-testid={`rep-surname-${index}`}
                        />
                        <Input
                          placeholder="Cédula (opcional)"
                          value={rep.id_number || ''}
                          onChange={(e) => updateRepresentative(index, 'id_number', e.target.value)}
                          data-testid={`rep-id-${index}`}
                        />
                      </div>
                      
                      {/* Photo upload */}
                      <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
                        <div className="flex-shrink-0">
                          {rep.photo_preview ? (
                            <img 
                              src={rep.photo_preview} 
                              alt={`${rep.name} preview`}
                              className="w-16 h-16 object-cover rounded-full border-2 border-primary"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm text-slate-600">Foto del representante (opcional)</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <label className="cursor-pointer">
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm hover:bg-slate-50 transition-colors">
                                <Camera className="w-4 h-4" />
                                {rep.photo_preview ? 'Cambiar foto' : 'Subir foto'}
                              </span>
                              <input 
                                type="file" 
                                accept="image/jpeg,image/png"
                                className="hidden"
                                onChange={(e) => handleLocalPhotoSelect(index, e)}
                                data-testid={`photo-input-${index}`}
                              />
                            </label>
                            {rep.photo_preview && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeLocalPhoto(index)}
                                className="text-red-500"
                              >
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirmation */}
      {step === 4 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Resumen de la Solicitud
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Datos de la Empresa</h4>
                  <dl className="space-y-1 text-sm">
                    <div><dt className="text-slate-500 inline">Razón Social:</dt> <dd className="inline">{formData.company_name}</dd></div>
                    <div><dt className="text-slate-500 inline">RUC:</dt> <dd className="inline">{formData.ruc}</dd></div>
                    <div><dt className="text-slate-500 inline">Línea:</dt> <dd className="inline">{formData.commercial_line}</dd></div>
                    <div><dt className="text-slate-500 inline">Categorías:</dt> <dd className="inline">{formData.product_categories.join(', ')}</dd></div>
                  </dl>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Datos del Firmante</h4>
                  <dl className="space-y-1 text-sm">
                    <div><dt className="text-slate-500 inline">Nombre:</dt> <dd className="inline">{formData.signer_name}</dd></div>
                    <div><dt className="text-slate-500 inline">Cédula:</dt> <dd className="inline">{formData.signer_id}</dd></div>
                    <div><dt className="text-slate-500 inline">Email:</dt> <dd className="inline">{formData.signer_email}</dd></div>
                  </dl>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-2">Representantes ({formData.representatives.length})</h4>
                <div className="grid grid-cols-2 gap-2">
                  {formData.representatives.map((rep, i) => (
                    <div key={i} className="text-sm bg-slate-50 p-2 rounded">
                      {rep.name} {rep.surname}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Detalle de Costos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{formData.num_credentials} credencial(es) × $15.00</span>
                  <span>${pricing.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>IVA (15%)</span>
                  <span>${pricing.ivaAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total a Pagar</span>
                  <span className="text-primary">${pricing.total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep} 
          disabled={step === 1}
          data-testid="prev-step-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        
        {step < 4 ? (
          <Button onClick={nextStep} data-testid="next-step-btn">
            Siguiente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading} data-testid="submit-btn">
            {loading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Enviando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Enviar Solicitud
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
